import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { helpArticleForRoute } from "../../../pages/Help/helpArticles";
import useVisibleHelpArticles from "../../../pages/Help/useVisibleHelpArticles";

/**
 * Topbar Help button. Deep-links to the guide for whatever page you're on and
 * falls back to the Help index when the page isn't documented.
 *
 * It lives here rather than in each page header because the header is only
 * rendered for non-fill pages (see DashboardLayout) — the topbar is on every
 * page, including Trip Manager and the kanban boards.
 */
export default function ContextualHelpButton() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const { articles } = useVisibleHelpArticles();

  const article = useMemo(
    () => helpArticleForRoute(location.pathname, articles),
    [location.pathname, articles]
  );

  const target = article ? `/help/${article.slug}` : "/help";
  const label = article ? `${t("Help")}: ${article.title}` : t("Help");

  return (
    <button
      type="button"
      onClick={() => navigate(target)}
      title={label}
      className="relative inline-flex items-center gap-x-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
    >
      <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
      <span className="hidden md:inline">{t("Help")}</span>
      <span className="sr-only">{label}</span>
    </button>
  );
}
